// P2b tanda 1 — forma del formulario de producto y mapeos del borrador local.
// El borrador vive en Mongo (ProductDraft) como { wcProductId, payload } donde
// payload ES este formulario. No hay nada de WooCommerce acá: estos mapeos no
// hacen red. El borrador nunca publica; escribir en WooCommerce llega en tanda 2.

import { TIPOS_PRODUCTO_DEFAULT, tipoIdPorArquetipo, tipoSugeridoPorCategorias } from './tiposProducto';
import { esTagColeccion } from './tagsColeccion';
import { bogotaNaiveAInputLocal } from './colTimeUtils';

export function emptyForm() {
  return {
    name: '',
    shortDescription: '',
    description: '',
    type: 'simple',              // 'simple' | 'variable'
    // Descripción larga guiada (se componen en `description` al previsualizar/guardar):
    descParrafo: '',             // 2-4 líneas: qué es, para qué, qué lo hace especial
    descDetalles: '',            // un detalle por línea → lista <ul>
    descCierre: '',              // tercer bloque; el título lo define el tipo de producto configurado
    regularPrice: '',
    salePrice: '',
    saleFrom: '',
    saleTo: '',
    // P16 (Programados): fecha/hora de publicación programada, en hora Bogotá y
    // formato de <input datetime-local> ("2026-11-01T18:58"). Solo cuenta cuando
    // el estado es «Programada» (future).
    fechaProgramada: '',
    sku: '',
    slug: '', // P3-9a: nombre SEO del producto, para el zip de fotos
    gtin: '',
    manageStock: true,
    stockQty: '',
    backorders: 'no',
    weight: '',
    length: '',
    width: '',
    height: '',
    // categoryIds = TODAS las categorías del producto (árbol con casillas, tanda 2b).
    // parentCatId/subCatId quedan como LEGADO (no los usa la UI ni el guardado).
    categoryIds: [],
    parentCatId: '',
    subCatId: '',
    brandId: '',
    // P3-4: collectionTagIds = ids de las COLECCIONES (tags coleccion-*)
    // elegidas; tagsRaw = TODAS las etiquetas del producto (con slug), para
    // preservar las normales (Sale) al guardar vía mergeTagsColeccion.
    collectionTagIds: [],
    tagsRaw: [],
    // Id del TIPO DE PRODUCTO configurado (tanda 4c: la lista vive en la config
    // de la tienda; la semilla en utils/tiposProducto.js). El default es el
    // PRIMER tipo de la semilla — mismo valor que resuelve productToForm para
    // un producto sin señal, así el comparador del banner no ve un "difiere"
    // fabricado entre un borrador nuevo y la base de Woo.
    productKind: TIPOS_PRODUCTO_DEFAULT[0].id,
    // Tanda 4: contenido del PADRE (meta _vitrina_contenido, {valor,unidad});
    // vacío = sin contenido declarado (las variaciones pueden traer el suyo).
    contenidoValor: '',
    contenidoUnidad: 'ml',
    // Tanda 5: guía de tallas. guiaId '' = sin asignación directa; medidas
    // null = sin tabla propia. Solo viajan TOCADAS (y solo en edición): el
    // GET del árbitro del theme es quien dice el estado real, no este form.
    guiaId: '',
    medidas: null,
    seoTitle: '',
    seoMetadesc: '',
    // Tanda 7: "Combínalo con" — ids de cross_sell_ids (máx. 3 en la UI).
    // Solo viaja TOCADO (regla 3g); [] tocado limpia la lista en Woo.
    crossSellIds: [],
    images: [],                  // [{ id, src, alt }] — principal = images[0] (tanda 3)
  };
}

// Compone la descripción larga guiada (párrafo + detalles en lista + cierre) en
// HTML, como pide la guía B.3. Función pura (sin red). Si hay parrafo/detalles/
// cierre se arma desde ellos; si no, cae al texto libre de `description`.
export function composeDescription(form) {
  const f = form || {};
  const partes = [];
  if ((f.descParrafo || '').trim()) partes.push(`<p>${f.descParrafo.trim()}</p>`);
  const items = (f.descDetalles || '').split('\n').map((s) => s.trim()).filter(Boolean);
  if (items.length) partes.push(`<ul>${items.map((i) => `<li>${i}</li>`).join('')}</ul>`);
  if ((f.descCierre || '').trim()) partes.push(`<p>${f.descCierre.trim()}</p>`);
  if (partes.length) return partes.join('\n');
  return (f.description || '').trim();
}

// Cuerpo que se manda al endpoint de borradores. El form entero va en payload.
export function formToDraftBody(form, wcProductId = null) {
  return {
    wcProductId: wcProductId == null ? null : Number(wcProductId),
    payload: { ...emptyForm(), ...form },
  };
}

// Reconstruye el formulario desde un documento de borrador (retomar). Completa
// con emptyForm para que un payload viejo/incompleto no rompa la vista.
export function draftToForm(draftDoc) {
  const payload = (draftDoc && draftDoc.payload) || {};
  return { ...emptyForm(), ...payload };
}

// Tanda 3e (defectos 1 y 2): el borrador local solo se OFRECE sobre un producto
// de Woo si es MÁS RECIENTE que la última modificación en Woo Y su contenido
// DIFIERE. La base de la ficha es siempre el GET del producto; esto solo decide
// si aparece el banner "Tienes un borrador local".
// Tanda 3f (defecto 3): la comparación cubre TODOS los campos del formulario y
// EXCLUYE solo los derivados y legados — la versión de 3e ENUMERABA qué
// comparar y dejó fuera campos editables (productKind, marca, colecciones):
// un cambio solo ahí se evaluaba "igual" y el banner se suprimía en silencio.
// Excluidos: descParrafo/Detalles/Cierre (derivados de `description`, meterían
// falsos "difiere" de formato), parentCatId/subCatId (legados sin UI) e
// `images` (se compara aparte por id+alt, para no arrastrar ruido de `src`).
const CAMPOS_NO_COMPARADOS = new Set([
  'descParrafo', 'descDetalles', 'descCierre', 'parentCatId', 'subCatId', 'images',
]);
export function draftEsOfrecible(draftDoc, wooModifiedGmt, formWoo) {
  if (!draftDoc) return false;
  const tDraft = Date.parse(draftDoc.updatedAt || '') || 0;
  const s = String(wooModifiedGmt || '');
  // Woo entrega date_modified_gmt SIN zona ("2026-09-13T14:00:00"): es UTC y se
  // le fija la Z — sin eso Date.parse la lee como hora LOCAL y corre el corte.
  const tWoo = s ? (Date.parse(/[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`) || 0) : 0;
  if (tDraft <= tWoo) return false;
  if (!formWoo) return true; // sin base que comparar (el GET falló): mejor ofrecer que perder
  const fd = draftToForm(draftDoc);
  const imagenClave = (i) => ({ id: i.id, alt: i.alt || '' });
  const igual = Object.keys(emptyForm())
    .filter((k) => !CAMPOS_NO_COMPARADOS.has(k))
    .every((k) => JSON.stringify(fd[k]) === JSON.stringify(formWoo[k]))
    && JSON.stringify((fd.images || []).map(imagenClave)) === JSON.stringify((formWoo.images || []).map(imagenClave));
  return !igual;
}

function metaValue(meta, key) {
  const row = (meta || []).find((m) => m.key === key);
  return row ? String(row.value == null ? '' : row.value) : '';
}

// Tanda 3h (hallazgo del e2e contra Woo real): WordPress le aplica wpautop a
// short_description y el GET devuelve "<p>texto</p>\n" — el form la trata como
// texto plano, así que se des-envuelve al cargar (sin esto el usuario ve HTML
// crudo y el comparador del banner marca "difiere" falso en cada round-trip).
function textoPlanoCorto(s) {
  return String(s || '').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();
}

// Mapea un producto de WooCommerce (GET /api/products/:id/full) al formulario,
// resolviendo categoría padre + hoja contra el catálogo de categorías.
// Tanda 4c: `tipos` = los tipos de producto CONFIGURADOS de la tienda (default:
// la semilla) — deciden el mapeo arquetipo→tipo y la deducción por categorías.
export function productToForm(full, catalog = [], tipos = TIPOS_PRODUCTO_DEFAULT) {
  const f = emptyForm();
  if (!full) return f;

  f.name = full.name || '';
  f.description = full.description || '';
  f.shortDescription = textoPlanoCorto(full.short_description);
  f.type = full.type || 'simple';
  f.regularPrice = full.regular_price != null ? String(full.regular_price) : '';
  f.salePrice = full.sale_price != null ? String(full.sale_price) : '';
  f.sku = full.sku || '';
  f.slug = full.slug || '';
  f.gtin = full.global_unique_id || '';
  // P16 (Programados): si está «Programada» (future), precargar la fecha/hora
  // desde date_created (que en future ES la fecha programada, hora Bogotá).
  if (full.status === 'future') f.fechaProgramada = bogotaNaiveAInputLocal(full.date_created);
  f.manageStock = full.manage_stock !== false;
  f.stockQty = full.stock_quantity != null ? String(full.stock_quantity) : '';
  f.backorders = full.backorders || 'no';
  f.weight = full.weight != null ? String(full.weight) : '';
  const dim = full.dimensions || {};
  f.length = dim.length != null ? String(dim.length) : '';
  f.width = dim.width != null ? String(dim.width) : '';
  f.height = dim.height != null ? String(dim.height) : '';

  // Categorías: TODAS las actuales (tanda 2b, árbol con casillas). Refleja el GET
  // 1-a-1, sin suponer una sola categoría por producto.
  const ids = (full.categories || []).map((c) => c.id);
  f.categoryIds = [...ids];
  // LEGADO (no se usa en la UI ni el guardado; se conserva para compatibilidad):
  const matched = ids.map((id) => catalog.find((c) => c.id === id)).filter(Boolean);
  const sub = matched.find((c) => c.parent !== 0);
  const parent = matched.find((c) => c.parent === 0);
  if (sub) { f.subCatId = String(sub.id); f.parentCatId = String(sub.parent); }
  else if (parent) { f.parentCatId = String(parent.id); }
  else if (ids.length) { f.parentCatId = String(ids[0]); }

  f.brandId = (full.brands && full.brands[0]) ? String(full.brands[0].id) : '';
  // P3-4: las colecciones seleccionadas son las etiquetas con slug coleccion-*;
  // tagsRaw conserva TODAS (con slug) para preservar las normales al guardar.
  f.tagsRaw = (full.tags || []).map((t) => ({ id: t.id, name: t.name, slug: t.slug }));
  f.collectionTagIds = f.tagsRaw.filter(esTagColeccion).map((t) => t.id);
  f.crossSellIds = (full.cross_sell_ids || []).map(Number);
  f.images = (full.images || []).map((i) => ({ id: i.id, src: i.src, alt: i.alt || '', optimizada: !!i.optimizada }));

  f.seoTitle = metaValue(full.meta_data, '_yoast_wpseo_title');
  f.seoMetadesc = metaValue(full.meta_data, '_yoast_wpseo_metadesc');

  // Tanda 4: contenido del padre — la meta llega como OBJETO {valor,unidad}
  // (contrato v0.7.155; un string caería a vacío sin error, como sus lectores).
  const cont = (full.meta_data || []).find((m) => m.key === '_vitrina_contenido');
  if (cont && cont.value && typeof cont.value === 'object') {
    f.contenidoValor = cont.value.valor != null ? String(cont.value.valor) : '';
    f.contenidoUnidad = cont.value.unidad || 'ml';
  }

  // Tanda 3h (ítem 1): el Tipo de producto PERSISTE en _vitrina_arquetipo
  // (contrato v0.7.155 — la meta manda sobre el mapa de categorías). Sin la
  // meta se DEDUCE de las categorías o sus ancestros (tanda 4c: contra las
  // claves configuradas de cada tipo, no contra nombres fijos); el valor
  // deducido es SUGERIDO (la ficha no lo marca tocado, así que no viaja hasta
  // que el usuario elija). La heurística vieja por brands se retiró: brands
  // está vacío hasta P2-tax y el contrato define la deducción por categorías.
  const arquetipo = metaValue(full.meta_data, '_vitrina_arquetipo');
  f.productKind = tipoIdPorArquetipo(tipos, arquetipo)
    || tipoSugeridoPorCategorias(tipos, full.categories, catalog)
    || ((tipos[0] && tipos[0].id) || '');

  return f;
}
